import { BACKDROPS, CLASSES, PALETTE, type ClassDef, type ClassId } from "@eldrun/content";
import { Box3, Color, Mesh, PerspectiveCamera, PlaneGeometry, Scene, Vector3, type WebGLRenderer } from "three";
import type { AvatarSignals } from "../game/avatar-state.ts";
import { createAvatarView, type AvatarView } from "../game/avatar-view.ts";
import { heldMesh } from "../game/held-item.ts";
import { applyAtmosphere, createAtmosphereUniforms, sampleAtmosphere } from "../render/atmosphere.ts";
import { avatars } from "../render/avatar-library.ts";
import { models } from "../render/model-library.ts";
import { PostFx } from "../render/post/post-fx.ts";
import { createSlotRing } from "../render/shaders/slot-ring.ts";
import { NeonMaterials, unindexed } from "../render/style/neon.ts";
import { gameColor, setWorldAccent } from "../render/style/roles.ts";
import { stagePlacement, type Rect, type StageCamera } from "../ui/screens/class-select-model.ts";

/**
 * Palco da escolha da classe (F85): os quatro personagens de verdade da partida (o mesmo
 * `createAvatarView` da `GameScene`: o GLB dos avatares, o tingimento e o material neon do herói; sem o
 * pacote, o boneco procedural), na cor de cada classe, acesos pela atmosfera de sempre e parados no
 * "parado" da máquina de estados do jogo. O DOM por cima cuida da entrada (mouse, teclado, controle); o
 * palco só mostra: `focus` destaca (vira para a câmera, cresce, o anel acende e o neon vai ao pico),
 * `choose` marca a escolhida e toca o golpe uma vez.
 */

/** A arma da key art de cada classe, na mão (a mesma família da afinidade). */
const WEAPONS: Readonly<Record<ClassId, string>> = { baluarte: "espada-prata", lanceiro: "lanca-prata", arcanista: "cajado-prata", patrulheiro: "arco-prata" };

/** Um pouco de cima: o chão e o anel sob os pés aparecem. */
const CAMERA: StageCamera = { fov: 30, dist: 11, y: 3.4, pitch: 0.21 };
/** O boneco olha para −Z; virado meia volta, olha para a câmera (que fica em +Z). */
const FACING = Math.PI;
/** De lado, cada um vira um pouco para o meio da tela. */
const TURN = 0.32;
/** O golpe da escolha (o mesmo progresso do golpe na partida), em ms. */
const FLOURISH_MS = 650;

const IDLE: AvatarSignals = { speed: 0, vy: 0, grounded: true, crouching: false, swimming: false, sliding: false, dead: false, swing: 0, drawing: false, eating: false, bites: 0, hurtAgo: Infinity, revivedAgo: Infinity };

interface Hero {
  readonly def: ClassDef;
  readonly view: AvatarView;
  readonly height: number;
  ring: Mesh | undefined;
  /** Destaque de 0 a 1, suavizado. */
  glow: number;
  placement: { x: number; y: number; scale: number };
  chosenAt: number;
}

export interface ClassStageProbe {
  models: { id: ClassId; model: string; clip: string; highlighted: boolean; chosen: boolean; ring: boolean; x: number; scale: number }[];
  focused: ClassId | undefined;
  chosen: ClassId | undefined;
  frames: number;
}

export interface ClassStage {
  /** Destaca a classe sob o mouse ou o foco (`undefined`: nenhuma). */
  focus(id: ClassId | undefined): void;
  /** A classe escolhida (`n`: o número do jogador nas marcas do anel; `animate`: o golpe, só quando é uma escolha agora). */
  choose(id: ClassId | undefined, n?: number, animate?: boolean): void;
  /** O palco de cada classe na tela (px CSS), para pôr cada modelo sob a coluna dela. */
  layout(rects: Partial<Record<ClassId, Rect>>): void;
  probe(): ClassStageProbe;
  stop(): void;
}

/**
 * Abre o palco: espera o pacote dos avatares (com reserva) e começa a desenhar no `renderer`. Se a tela
 * fechar antes (`signal`), não toca no `renderer` (a partida pode já ter o laço dela) e devolve `undefined`.
 */
export async function startClassStage(renderer: WebGLRenderer, signal?: AbortSignal): Promise<ClassStage | undefined> {
  await Promise.all([avatars.preload(), models.preload()]);
  if (signal?.aborted) return undefined;
  const scene = new Scene();
  scene.background = new Color(BACKDROPS.fundo);
  const atmosphere = createAtmosphereUniforms();
  const sample = sampleAtmosphere(0.5);
  const materials = new NeonMaterials(atmosphere);
  const camera = new PerspectiveCamera(CAMERA.fov, 1, 0.1, 200);
  camera.position.set(0, CAMERA.y, CAMERA.dist);
  camera.rotation.set(-(CAMERA.pitch ?? 0), 0, 0);

  // Chão low-poly: a malha dá a profundidade e segue o acento do mundo (Fase 13, ADR 0026): neutra até
  // uma classe ficar em destaque ou escolhida, e então na cor dela.
  const ground = new Mesh(unindexed(new PlaneGeometry(60, 30, 24, 12).rotateX(-Math.PI / 2)), materials.get("terreno"));
  scene.add(ground);

  const heroes: Hero[] = CLASSES.map((def, i) => {
    const hex = PALETTE.classes[def.id].hex;
    const view = createAvatarView(avatars.library, atmosphere, hex, def.character, i);
    const library = models.library;
    const weapon = library ? heldMesh(WEAPONS[def.id], library, materials.get("arma-heroi", hex), view.heldView) : undefined;
    view.setHeld(weapon, WEAPONS[def.id]);
    view.update({ dt: 0, now: 0, yaw: FACING, pitch: 0, signals: IDLE, armSwing: 0, diedAt: -1, distance: 5, visible: true, frame: 0, range: 1 });
    view.root.updateMatrixWorld(true);
    const height = new Box3().setFromObject(view.root).getSize(new Vector3()).y || 1.8;
    scene.add(view.root);
    return { def, view, height, ring: undefined, glow: 0, placement: { x: (i - 1.5) * 1.8, y: 0, scale: 1 }, chosenAt: -Infinity };
  });

  let focused: ClassId | undefined;
  let chosen: ClassId | undefined;
  const accent = () => {
    const id = focused ?? chosen;
    setWorldAccent(id ? PALETTE.classes[id].hex : undefined);
  };
  let chosenN = 1;
  let rects: Partial<Record<ClassId, Rect>> = {};
  let frames = 0;

  const ringFor = (hero: Hero, on: boolean) => {
    if (hero.ring) hero.view.root.remove(hero.ring);
    hero.ring = on ? createSlotRing(atmosphere, gameColor(PALETTE.classes[hero.def.id].hex), chosenN) : undefined;
    if (hero.ring) hero.view.root.add(hero.ring);
  };
  const place = () => {
    const viewport = { w: window.innerWidth, h: window.innerHeight };
    for (const hero of heroes) {
      const rect = rects[hero.def.id];
      if (rect) hero.placement = stagePlacement(viewport, CAMERA, rect, hero.height);
    }
    // O chão acompanha os pés (as colunas ficam na mesma altura).
    ground.position.y = Math.min(...heroes.map((h) => h.placement.y));
  };

  const post = new PostFx(renderer);
  post.bind(atmosphere);
  const resize = () => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    place();
  };
  window.addEventListener("resize", resize);
  resize();

  let last = performance.now();
  let elapsed = 0;
  renderer.setAnimationLoop((now: number) => {
    const dt = Math.min(0.1, (now - last) / 1000);
    last = now;
    elapsed += dt;
    frames++;
    for (const hero of heroes) {
      const id = hero.def.id;
      const lit = id === focused || id === chosen;
      hero.glow += ((id === focused ? 1 : id === chosen ? 0.6 : 0) - hero.glow) * Math.min(1, dt * 10);
      const swing = Math.min(1, (now - hero.chosenAt) / FLOURISH_MS);
      const p = hero.placement;
      const turn = lit ? 0 : -Math.sign(p.x) * TURN;
      hero.view.update({ dt, now, yaw: FACING + turn, pitch: 0, signals: { ...IDLE, swing: swing < 1 ? swing : 0 }, armSwing: swing < 1 ? swing : 0, diedAt: -1, distance: 5, visible: true, frame: frames, range: 1 });
      const s = p.scale * (1 + 0.07 * hero.glow);
      hero.view.root.position.set(p.x, p.y, 0);
      hero.view.root.scale.setScalar(s);
      // O neon da costura do repouso ao pico no destaque (a vista aplica o "Reduzir lampejos").
      hero.view.setEnergy(hero.glow);
      if (lit !== (hero.ring !== undefined)) ringFor(hero, lit);
    }
    applyAtmosphere(atmosphere, sample, elapsed);
    post.render(scene, camera, dt);
  });

  return {
    focus(id) {
      focused = id;
      accent();
    },
    choose(id, n = 1, animate = true) {
      if (id !== chosen || n !== chosenN) {
        chosenN = n;
        for (const hero of heroes) if (hero.ring) ringFor(hero, true);
      }
      if (animate && id && id !== chosen) {
        const hero = heroes.find((h) => h.def.id === id);
        if (hero) hero.chosenAt = performance.now();
      }
      chosen = id;
      accent();
    },
    layout(next) {
      rects = next;
      place();
    },
    probe: () => ({
      models: heroes.map((h) => {
        const stats = h.view.stats();
        return { id: h.def.id, model: stats.model, clip: stats.clip, highlighted: h.def.id === focused, chosen: h.def.id === chosen, ring: h.ring !== undefined, x: h.placement.x, scale: h.placement.scale };
      }),
      focused,
      chosen,
      frames,
    }),
    stop() {
      renderer.setAnimationLoop(null);
      window.removeEventListener("resize", resize);
      setWorldAccent(undefined);
      // O canvas fica limpo (cada vista tem os próprios uniforms: o descarte leva o neon junto).
      renderer.setRenderTarget(null);
      renderer.clear();
      post.bind(undefined);
      post.dispose();
      for (const hero of heroes) hero.view.dispose();
      materials.dispose();
      ground.geometry.dispose();
    },
  };
}
